import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * The organisation's saved colours, shared by every colour picker on the page.
 *
 * A template editor renders six or more pickers at once and a table with styled
 * rows renders two per row, so a hook that fetched on mount would fire a burst
 * of identical requests every time a section opened. The palette is module
 * state instead: the first caller fetches, everyone else reads what is already
 * there, and a save notifies every mounted picker so they all repaint together
 * rather than drifting until remount.
 */

let cache = null;          // null = never loaded
let inFlight = null;       // de-duplicates concurrent first loads
const listeners = new Set();

function publish(colors) {
  cache = colors;
  listeners.forEach(fn => fn(colors));
}

export function useColorFavorites() {
  const { apiFetch, hasGroup } = useAuth();
  const [colors, setColors] = useState(cache || []);

  useEffect(() => {
    listeners.add(setColors);
    if (cache !== null) {
      setColors(cache);
    } else if (!inFlight) {
      inFlight = apiFetch('/report-templates/color-palette')
        .then(r => (r.ok ? r.json() : { colors: [] }))
        .then(d => publish(d.colors || []))
        // An empty palette is the right failure: the pickers still work from
        // their presets, which is how they worked before this existed.
        .catch(() => publish([]))
        .finally(() => { inFlight = null; });
    }
    return () => { listeners.delete(setColors); };
  }, [apiFetch]);

  // Only Report Admins may change the shared palette; everyone else sees it.
  const canEdit = hasGroup('Report Admins');

  const save = useCallback(async (next) => {
    const res = await apiFetch('/report-templates/color-palette', {
      method: 'PUT', body: JSON.stringify({ colors: next }),
    });
    if (!res.ok) return false;
    const d = await res.json();
    publish(d.colors || []);
    return true;
  }, [apiFetch]);

  const add = useCallback((color) => {
    if (!color) return Promise.resolve(false);
    const c = String(color).toLowerCase();
    if ((cache || []).includes(c)) return Promise.resolve(true);
    // Newest first: the colour you just chose is the one you are most likely
    // to want again in the next few clicks.
    return save([c, ...(cache || [])]);
  }, [save]);

  const remove = useCallback((color) =>
    save((cache || []).filter(c => c !== String(color).toLowerCase())), [save]);

  return { colors, canEdit, add, remove };
}

export default useColorFavorites;
